import { useMemo } from 'react';
import {
  getTotalLtcCoveragePayout,
  getTotalIndemnitySurplus,
  getRemainingDeathBenefit,
  getTotalNonLtcPayout,
  getTotalPayout,
  getTotalIneligibleCost,
  getTotalProjectedCareCost,
} from '@shared/calculations/funding/projection/projectionTotals';
import { CollapsibleSection } from './CollapsibleSection';
import { TooltipLabel } from './TooltipLabel';
import { currency } from './projectionTable/formatters';

interface ReturnsSummaryCardProps {
  projection: ClaimProjection;
}

interface Row {
  label: string;
  tooltip?: string;
  value: number;
  emphasis?: 'total' | 'sub';
}

/**
 * High-level payout totals derived from the new strategy-based projection
 * engine. Mirrors the LTC / Non-LTC split used by the legacy FundingTable so
 * the two views can be compared side-by-side during parity validation.
 *
 * ROI / CAGR / IRR are intentionally omitted in step 1 — they require premium
 * cashflow inputs that the projection engine does not yet model.
 */
export function ReturnsSummaryCard({ projection }: ReturnsSummaryCardProps) {
  const totals = useMemo(() => {
    const { months } = projection;
    const ltcCoverage = getTotalLtcCoveragePayout(months);
    const indemnitySurplus = getTotalIndemnitySurplus(months);
    const remainingDeathBenefit = getRemainingDeathBenefit(months);
    const nonLtc = getTotalNonLtcPayout(months);
    const total = getTotalPayout(months);
    const ineligible = getTotalIneligibleCost(months);
    const projectedCareCost = getTotalProjectedCareCost(months);
    return {
      ltcCoverage,
      indemnitySurplus,
      remainingDeathBenefit,
      nonLtc,
      total,
      ineligible,
      projectedCareCost,
    };
  }, [projection]);

  const ltcRows: Row[] = [
    {
      label: 'LTC payout',
      tooltip:
        'Dollars the policy applied to care cost: ltcPayoutToCost (direct coverage plus any banked indemnity surplus later drained to cover a shortfall) + any retroactive EP lump sum.',
      value: totals.ltcCoverage,
      emphasis: 'total',
    },
  ];

  const nonLtcRows: Row[] = [
    {
      label: 'Indemnity surplus',
      tooltip:
        'Indemnity cash remaining at the end of the projection (the carried balance, not gross accrual). Surplus later drained to cover care cost is counted under LTC payout; only the never-used remainder is non-LTC. Matches legacy ltcPolicy.indemnitySurplus.',
      value: totals.indemnitySurplus,
      emphasis: 'sub',
    },
    {
      label: 'Remaining death benefit',
      tooltip:
        'Death benefit pool remaining at the end of the projection horizon. Paid to beneficiaries; counts as a non-LTC return.',
      value: totals.remainingDeathBenefit,
      emphasis: 'sub',
    },
    {
      label: 'Non-LTC payout',
      tooltip: 'Indemnity surplus + remaining death benefit.',
      value: totals.nonLtc,
      emphasis: 'total',
    },
  ];

  const summaryRows: Row[] = [
    {
      label: 'Total projected LTC cost',
      tooltip:
        'Inflation-adjusted care cost across the entire projection, regardless of who pays. Sum of effectiveCareCost per month.',
      value: totals.projectedCareCost,
    },
    {
      label: 'Total payout',
      tooltip: 'LTC payout + Non-LTC payout. Mirrors policyTotalValue in the legacy engine.',
      value: totals.total,
      emphasis: 'total',
    },
    {
      label: 'Ineligible cost (out-of-pocket)',
      tooltip:
        'Net care cost not covered by the policy across the projection. Includes months in waiting period or after pool exhaustion.',
      value: totals.ineligible,
    },
  ];

  return (
    <CollapsibleSection
      title="Returns summary"
      subtitle="Aggregated payouts from the new projection engine"
      defaultOpen={true}
    >
      <div className="grid grid-cols-1 gap-x-8 gap-y-1 md:grid-cols-2">
        <RowGroup heading="LTC" rows={ltcRows} />
        <RowGroup heading="Non-LTC" rows={nonLtcRows} />
        <RowGroup heading="Summary" rows={summaryRows} className="md:col-span-2" />
      </div>
      <p className="mt-4 text-[11px] text-gray-500">
        ROI, CAGR, and IRR are not yet computed by the new engine. Compare these payout totals
        against the legacy Funding table to spot-check parity.
      </p>
    </CollapsibleSection>
  );
}

function RowGroup({
  heading,
  rows,
  className,
}: {
  heading: string;
  rows: Row[];
  className?: string;
}) {
  return (
    <section className={className}>
      <h3 className="mb-1.5 border-b border-gray-200 pb-1 text-[11px] font-semibold uppercase tracking-wide text-gray-700">
        {heading}
      </h3>
      <dl className="divide-y divide-gray-100">
        {rows.map(row => (
          <div
            key={row.label}
            className="flex items-baseline justify-between gap-4 py-1.5 text-sm"
          >
            <dt
              className={
                row.emphasis === 'total'
                  ? 'font-medium text-gray-900'
                  : row.emphasis === 'sub'
                    ? 'pl-3 text-gray-600'
                    : 'text-gray-700'
              }
            >
              {row.tooltip ? <TooltipLabel label={row.label} tooltip={row.tooltip} /> : row.label}
            </dt>
            <dd
              className={`tabular-nums ${
                row.emphasis === 'total' ? 'font-semibold text-gray-900' : 'text-gray-800'
              }`}
            >
              {currency(row.value)}
            </dd>
          </div>
        ))}
      </dl>
    </section>
  );
}
